import { create } from 'zustand';
import { i18n } from '../i18n';
import { getMeTimeClock } from '../api/generated/me-time-clock/me-time-clock';
import { getEmployeeOvertime } from '../api/generated/employee-overtime/employee-overtime';
import type { FacilityFlowCoreModulesPlanningDtosOvertimeOvertimeBalanceDto } from '../api/generated/models';
import type { OvertimeBalanceDto } from '../types';
import { useToastStore } from './useToastStore';

interface OvertimeState {
  /** Überstundenkonto des angemeldeten Mitarbeiters. */
  myBalance: OvertimeBalanceDto | null;
  /** Konten je Mitarbeiter (Verwaltungssicht). */
  balancesByEmployee: Record<string, OvertimeBalanceDto>;
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchMyBalance: (referenceMonth?: string) => Promise<void>;
  fetchEmployeeBalance: (employeeId: string, referenceMonth?: string) => Promise<void>;
  /**
   * Stichtag und Eröffnungssaldo in einem Schritt. Alles vor dem Stichtag bleibt aus dem
   * Konto heraus — das ist der Weg gegen ein Phantom-Minus aus nicht importierter Historie.
   */
  setUpAccount: (
    employeeId: string,
    startDate: string,
    openingBalanceHours: number,
    note?: string,
  ) => Promise<void>;
}

const meApi = getMeTimeClock();
const employeeOvertimeApi = getEmployeeOvertime();

const t = (key: string) => i18n.t(key, { ns: 'timeclock' }) as string;

// why: eine Mapping-Stelle — sonst fehlt ein neues Feld in einem der drei Aufrufpfade.
const toBalance = (
  dto: FacilityFlowCoreModulesPlanningDtosOvertimeOvertimeBalanceDto,
): OvertimeBalanceDto => ({
  employeeId: dto.employeeId ?? '',
  periodStart: dto.periodStart ?? '',
  periodEnd: dto.periodEnd ?? '',
  workedHours: dto.workedHours ?? 0,
  creditedHours: dto.creditedHours ?? 0,
  targetHours: dto.targetHours ?? 0,
  compensationHours: dto.compensationHours ?? 0,
  adjustmentHours: dto.adjustmentHours ?? 0,
  balanceHours: dto.balanceHours ?? 0,
  hasConfiguredStartDate: dto.hasConfiguredStartDate ?? false,
  months: (dto.months ?? []).map((m) => ({
    year: m.year ?? 0,
    month: m.month ?? 0,
    workedHours: m.workedHours ?? 0,
    creditedHours: m.creditedHours ?? 0,
    targetHours: m.targetHours ?? 0,
    compensationHours: m.compensationHours ?? 0,
    adjustmentHours: m.adjustmentHours ?? 0,
    deltaHours: m.deltaHours ?? 0,
    closingBalanceHours: m.closingBalanceHours ?? 0,
  })),
  adjustments: (dto.adjustments ?? []).map((a) => ({
    overtimeAdjustmentId: a.overtimeAdjustmentId ?? '',
    effectiveDate: a.effectiveDate ?? '',
    adjustmentHours: a.adjustmentHours ?? 0,
    note: a.note ?? '',
    createdAt: a.createdAt ?? '',
    createdBy: a.createdBy ?? null,
  })),
});

export const useOvertimeStore = create<OvertimeState>((set, get) => ({
  myBalance: null,
  balancesByEmployee: {},
  loading: false,
  saving: false,
  error: null,

  fetchMyBalance: async (referenceMonth) => {
    set({ loading: true, error: null });
    try {
      const response = await meApi.getApiV1MeTimeclockOvertimeBalance(
        referenceMonth ? { referenceMonth } : undefined,
      );
      set({ myBalance: toBalance(response), loading: false });
    } catch {
      // Fehler-State statt Toast — die Seite zeigt das Konto dann einfach nicht an.
      set({ error: 'errors.overtimeFetchFailed', loading: false });
    }
  },

  fetchEmployeeBalance: async (employeeId, referenceMonth) => {
    set({ loading: true, error: null });
    try {
      const response = await employeeOvertimeApi.getApiV1EmployeesEmployeeIdOvertimeBalance(
        employeeId,
        referenceMonth ? { referenceMonth } : undefined,
      );
      set((state) => ({
        balancesByEmployee: { ...state.balancesByEmployee, [employeeId]: toBalance(response) },
        loading: false,
      }));
    } catch {
      set({ error: 'errors.overtimeFetchFailed', loading: false });
    }
  },

  setUpAccount: async (employeeId, startDate, openingBalanceHours, note) => {
    set({ saving: true, error: null });
    try {
      const response = await employeeOvertimeApi.postApiV1EmployeesEmployeeIdOvertimeAccountSetup(
        employeeId,
        { startDate, openingBalanceHours, note: note ?? null },
      );

      set((state) => ({
        balancesByEmployee: { ...state.balancesByEmployee, [employeeId]: toBalance(response) },
        saving: false,
      }));

      if (get().myBalance?.employeeId === employeeId) {
        await get().fetchMyBalance();
      }

      useToastStore.getState().addToast(t('overtime.messages.accountSetUp'), 'success');
    } catch (error) {
      set({ saving: false, error: 'errors.overtimeAdjustFailed' });
      throw error;
    }
  },

}));
